// src/components/Navigation.tsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Login as LoginIcon,
  AppRegistration as RegisterIcon,
  Logout as LogoutIcon,
  Group as GroupIcon,
  People as PeopleIcon,
} from "@mui/icons-material";

const Navigation: React.FC = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = () => {
    logout();
    // Close drawer if open
    if (drawerOpen) {
      setDrawerOpen(false);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = [
    {
      name: "Home",
      path: "/",
      icon: <HomeIcon />,
      auth: false,
    },
    {
      name: "Friends",
      path: "/friends",
      icon: <PeopleIcon />,
      auth: true,
    },
    {
      name: "Groups",
      path: "/groups",
      icon: <GroupIcon />,
      auth: true,
    },
    {
      name: "Profile",
      path: "/profile",
      icon: <PersonIcon />,
      auth: true,
    },
    {
      name: "Login",
      path: "/login",
      icon: <LoginIcon />,
      auth: false,
      hideWhenAuth: true,
    },
    {
      name: "Register",
      path: "/register",
      icon: <RegisterIcon />,
      auth: false,
      hideWhenAuth: true,
    },
  ];

  // Filter items based on auth status
  const filteredItems = navItems.filter((item) => {
    if (user) {
      return item.auth || (!item.auth && !item.hideWhenAuth);
    }
    return !item.auth;
  });

  // Drawer content
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
      <Divider />
      <List>
        {filteredItems.map((item) => (
          <ListItem key={item.name} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "rgba(230, 126, 34, 0.1)",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(230, 126, 34, 0.2)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "primary.main" }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.name} />
            </ListItemButton>
          </ListItem>
        ))}
        {user && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(230, 126, 34, 0.1)",
                },
              }}
            >
              <ListItemIcon sx={{ color: "primary.main" }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="static"
        sx={{
          bgcolor: "primary.main",
          boxShadow: 3,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo/Title - visible on all screens */}
            <Box
              component="img"
              src={"/logo512.png"}
              alt="Logo"
              sx={{
                height: 40, // Adjust the size as needed
                width: 40,
                mr: 1,
              }}
            />

            {/* Mobile menu icon */}
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            ) : (
              // Desktop navigation
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {filteredItems.map((item) => (
                  <Button
                    key={item.name}
                    component={Link}
                    to={item.path}
                    sx={{
                      my: 2,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      mx: 1,
                      backgroundColor: isActive(item.path)
                        ? "rgba(255, 255, 255, 0.1)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                      },
                    }}
                    startIcon={item.icon}
                  >
                    {item.name}
                  </Button>
                ))}
                {user && (
                  <Button
                    onClick={handleLogout}
                    sx={{
                      my: 2,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      mx: 1,
                    }}
                    startIcon={<LogoutIcon />}
                  >
                    Logout
                  </Button>
                )}
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navigation;
